import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Campo } from '../componentes/ui/formulario';
import { useTextoDeError } from '../componentes/ui/estados';

/**
 * Elegir la clave con un enlace de recuperación.
 *
 * Es pública: quien llega acá no puede entrar, justamente. El token viaja en la dirección, se usa
 * una vez y vence. La clave nunca pasa por quien generó el enlace.
 */
export function Recuperacion(): React.ReactElement {
  const { t } = useTranslation();
  const textoDeError = useTextoDeError();
  const token = new URLSearchParams(globalThis.location.search).get('token') ?? '';
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lista, setLista] = useState(false);

  const confirmar = useMutation({
    mutationFn: () =>
      pedir<void>('/acceso/recuperacion/confirmacion', {
        method: 'POST',
        cuerpo: { token, contrasena: nueva },
      }),
    onSuccess: () => {
      setError(null);
      setLista(true);
      // Se limpia el token de la barra: queda en el historial, y ya no sirve para nada bueno.
      globalThis.history.replaceState(null, '', '/recuperacion');
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="w-full max-w-md rounded-marco border border-borde bg-superficie p-6">
        <h1 className="mb-1 text-lg font-semibold tracking-tight">{t('recuperacion.titulo')}</h1>
        <p className="mb-5 text-sm text-texto-tenue">{t('recuperacion.ayuda')}</p>
        {token === '' && !lista ? (
          <p className="text-sm text-mal">{t('recuperacion.sinToken')}</p>
        ) : lista ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm">{t('recuperacion.listo')}</p>
            <Link to="/">
              <Boton tono="principal" className="w-full">
                {t('recuperacion.entrar')}
              </Boton>
            </Link>
          </div>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(evento) => {
              evento.preventDefault();
              if (nueva !== repetida) {
                setError(t('clave.noCoinciden'));
                return;
              }
              confirmar.mutate();
            }}
          >
            <Campo
              etiqueta={t('clave.nueva')}
              ayuda={t('clave.ayudaNueva')}
              type="password"
              autoComplete="new-password"
              obligatorio
              value={nueva}
              onChange={(evento) => {
                setNueva(evento.target.value);
              }}
            />
            <Campo
              etiqueta={t('clave.repetir')}
              type="password"
              autoComplete="new-password"
              obligatorio
              value={repetida}
              onChange={(evento) => {
                setRepetida(evento.target.value);
              }}
            />
            {error === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {error}
              </p>
            )}
            <Boton tono="principal" type="submit" cargando={confirmar.isPending}>
              {t('recuperacion.confirmar')}
            </Boton>
          </form>
        )}
      </div>
    </div>
  );
}
